import { useEffect, useState } from 'react';
import { Languages } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import {
  isLanguageActive,
  normalizeLanguageCode,
  setAuthLanguageOverride
} from '../utils/languageUtils';
import referenceService from '../services/referenceService';
import logger from '../utils/logger';

function AuthLanguageSelector({ className = '' }) {
  const { t, i18n } = useTranslation();
  const [languages, setLanguages] = useState([]);
  const [loading, setLoading] = useState(true);
  const selectedLanguage = normalizeLanguageCode(i18n.resolvedLanguage || i18n.language) || 'en';
  const selectedLanguageIsActive = isLanguageActive(selectedLanguage, languages);

  useEffect(() => {
    let isMounted = true;

    const loadLanguages = async () => {
      try {
        setLoading(true);
        const languageData = await referenceService.getLanguages();
        if (isMounted) {
          setLanguages((Array.isArray(languageData) ? languageData : [])
            .filter(language => language?.is_active !== 0 && language?.is_active !== false));
        }
      } catch (error) {
        logger.warn('Failed to load auth language options', error);
        if (isMounted) {
          setLanguages([]);
        }
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    };

    loadLanguages();

    return () => {
      isMounted = false;
    };
  }, []);

  const handleLanguageChange = async (event) => {
    const language = setAuthLanguageOverride(event.target.value, languages);
    if (!language) return;

    // Auth screens do not have a reliable entity/user preference context yet.
    // Persist only the anonymous i18next language and the session override
    // marker; authenticated preference saving happens during preference load.
    await i18n.changeLanguage(language);
  };

  return (
    <div className={`inline-flex items-center gap-2 text-sm text-gray-700 dark:text-gray-300 ${className}`}>
      <label htmlFor="auth-language-selector" className="inline-flex items-center gap-1 font-medium">
        <Languages className="h-4 w-4" aria-hidden="true" />
        <span>{t('preferredLanguage')}</span>
      </label>
      <select
        id="auth-language-selector"
        aria-label={t('selectLanguage')}
        value={selectedLanguageIsActive ? selectedLanguage : ''}
        onChange={handleLanguageChange}
        disabled={loading || languages.length === 0}
        className="rounded-md border border-gray-300 bg-white px-2 py-1 text-sm text-gray-900 shadow-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500 dark:border-gray-600 dark:bg-gray-700 dark:text-white"
      >
        {loading && <option value="">{t('Loading...')}</option>}
        {!loading && languages.length === 0 && (
          <option value="">{t('Error loading languages')}</option>
        )}
        {!loading && languages.length > 0 && !selectedLanguageIsActive && (
          <option value="" disabled>
            {t('selectLanguage')}
          </option>
        )}
        {!loading && languages.map(language => (
          <option key={language.language_code} value={language.language_code}>
            {language.language_name}
          </option>
        ))}
      </select>
    </div>
  );
}

export default AuthLanguageSelector;
